import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Space, Typography, message } from 'antd';
import { api } from '../lib/api';

const { Title, Text } = Typography;

type Invitation = {
  id: string;
  email: string;
  organization: {
    id: string;
    name: string;
    slug: string;
    description?: string | null;
  };
  invitedBy: {
    name: string | null;
    email: string;
  };
  expiresAt: string;
};

export default function InvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      setRequiresAuth(false);
      const sessionUser = await api.auth.getSession().catch(() => null);
      if (!sessionUser) {
        setRequiresAuth(true);
      }
      const data = await api.invitations.get(token!);
      setInvitation(data.invitation);
    } catch (error: any) {
      if (error?.status === 401) {
        setRequiresAuth(true);
        return;
      }
      message.error(error?.message || 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!token) return;
    try {
      setProcessing(true);
      if (requiresAuth) {
        navigate(
          `/signup?email=${encodeURIComponent(invitation?.email || '')}&invite=${encodeURIComponent(token)}&return=${encodeURIComponent(`/invitations/${token}`)}`
        );
        return;
      }

      await api.invitations.accept(token);
      message.success('Invitation accepted');
      navigate('/apps');
    } catch (error: any) {
      if (error?.status === 401) {
        setRequiresAuth(true);
        return;
      }
      message.error(error?.message || 'Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!token) return;
    try {
      setProcessing(true);
      if (requiresAuth) {
        await api.invitations.declinePublic(token);
        message.success('Invitation declined');
        navigate('/login');
        return;
      }

      await api.invitations.decline(token);
      message.success('Invitation declined');
      navigate('/organizations');
    } catch (error: any) {
      if (error?.status === 401) {
        setRequiresAuth(true);
        return;
      }
      message.error(error?.message || 'Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <Card loading />;
  }

  if (!invitation) {
    return (
      <Card>
        <Text type="secondary">Invitation not found or expired.</Text>
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={3}>Organization Invitation</Title>
        <Text>
          You were invited to join <strong>{invitation.organization.name}</strong>.
        </Text>
        <Text type="secondary">
          Invited by {invitation.invitedBy.name || invitation.invitedBy.email}
        </Text>
        <Text type="secondary">
          Expires on {new Date(invitation.expiresAt).toLocaleDateString()}
        </Text>
        {requiresAuth ? (
          <Space>
            <Button
              type="primary"
              onClick={() => {
                navigate(
                  `/signup?email=${encodeURIComponent(invitation.email)}&invite=${encodeURIComponent(token || '')}&return=${encodeURIComponent(`/invitations/${token}`)}`
                );
              }}
              loading={processing}
            >
              Sign Up to Accept
            </Button>
            <Button
              onClick={() => {
                navigate(`/login?return=${encodeURIComponent(`/invitations/${token}`)}`);
              }}
            >
              Sign In
            </Button>
            <Button onClick={handleDecline} loading={processing}>
              Decline
            </Button>
          </Space>
        ) : (
          <Space>
            <Button type="primary" onClick={handleAccept} loading={processing}>
              Accept
            </Button>
            <Button onClick={handleDecline} loading={processing}>
              Decline
            </Button>
          </Space>
        )}
      </Space>
    </Card>
  );
}
